<template>
  <div class="sifcop-container">
    <div class="headerBlock">
      <div>
        <h1 class="primaryFont title">Nueva Acta</h1>
        <p class="secondaryFont subtitle">Formulario de digitalización SIFCOP – Convenio Policía</p>
      </div>
    </div>

    <div v-if="isLoading" class="loading-block">
      <ProgressSpinner />
    </div>

    <div v-else-if="fetchError" class="error-block">
      <p class="error-text">{{ fetchError }}</p>
      <Button label="Volver al listado" @click="handleBack" class="back-btn" />
    </div>

    <template v-else-if="acta">
      <div v-if="isEditing" class="edit-mode-container">
        <div class="edit-actions">
          <Button class="secondaryButton button" @click="handleCancelEdit" :disabled="isBusy">
            <div class="flex align-items-center w-full gap-2 justify-content-start">
              <X style="width: 20px; height: 20px" />
              <span class="ml-2">Cancelar</span>
            </div>
          </Button>

          <Button
            class="button primaryButton"
            @click="handleSaveEdit"
            :disabled="isBusy || !sifcopStore.isFormValid"
          >
            <div v-if="!isBusy" class="flex align-items-center gap-2 justify-content-center">
              <Check style="width: 20px; height: 20px" />
              <span>Guardar</span>
            </div>
            <div v-else class="flex align-items-center gap-2 justify-content-center">
              <i class="pi pi-spin pi-spinner mr-2" style="font-size: 1rem" />
              <span>Guardando...</span>
            </div>
          </Button>
        </div>

        <SifcopForm :showPdfButton="false" />
      </div>

      <div v-else class="flex justify-content-center flex-column gap-5">
        <!-- SECCIÓN: Datos Generales -->
        <div class="section-header">
          <span class="section-title">Datos General</span>
          <div class="section-actions">
            <Button class="action-icon-btn" @click="handleDelete">
              <div class="flex align-items-center gap-2 justify-content-center">
                <Trash style="width: 20px; height: 20px" />
              </div>
            </Button>
            <Button class="action-edit-btn" @click="handleEdit">
              <div class="flex align-items-center gap-2 justify-content-center w-full">
                <Pencil style="width: 20px; height: 20px" />
                <span>Editar</span>
              </div>
            </Button>
            <!-- <Button class="action-pdf-btn" @click="handleGeneratePdf" :disabled="isGeneratingPdf">
              <div class="flex align-items-center gap-2 justify-content-center w-full">
                <FileText style="width: 20px; height: 20px" />
                <span>{{ isGeneratingPdf ? "Generando..." : "Generar PDF" }}</span>
              </div>
            </Button> -->
            <Button class="pdfButton" @click="handleGeneratePdf" :disabled="isGeneratingPdf">{{
              isGeneratingPdf ? "Generando PDF..." : "Generar PDF"
            }}</Button>
          </div>
        </div>

        <div class="section-card">
          <div class="section-body">
            <div class="fields-grid">
              <div class="field-item">
                <span class="field-label">Organismo Interviniente</span>
                <span class="field-value">{{
                  acta.datosGenerales.organismoInterviniente || "-"
                }}</span>
              </div>
              <div class="field-item">
                <span class="field-label">Dependencia Policial</span>
                <span class="field-value">{{
                  acta.datosGenerales.dependenciaPolicial || "-"
                }}</span>
              </div>
              <div class="field-item">
                <span class="field-label">Lugar</span>
                <span class="field-value">{{ acta.datosGenerales.lugar || "-" }}</span>
              </div>
              <div class="field-item">
                <span class="field-label">Departamento</span>
                <span class="field-value">{{ acta.datosGenerales.departamento || "-" }}</span>
              </div>
              <div class="field-item">
                <span class="field-label">Provincia</span>
                <span class="field-value">{{ acta.datosGenerales.provincia || "-" }}</span>
              </div>
              <div class="field-item">
                <span class="field-label">Fecha</span>
                <span class="field-value">{{ formatDate(acta.datosGenerales.fecha) }}</span>
              </div>
              <div class="field-item">
                <span class="field-label">Hora</span>
                <span class="field-value">{{ acta.datosGenerales.hora || "-" }}</span>
              </div>
              <div class="field-item">
                <span class="field-label">Día</span>
                <span class="field-value">{{ acta.datosGenerales.dia || "-" }}</span>
              </div>
              <div class="field-item">
                <span class="field-label">Mes</span>
                <span class="field-value">{{ acta.datosGenerales.mes || "-" }}</span>
              </div>
              <div class="field-item">
                <span class="field-label">Año</span>
                <span class="field-value">{{ acta.datosGenerales.anio || "-" }}</span>
              </div>
              <div class="field-item">
                <span class="field-label">Funcionario Policial que suscribe</span>
                <span class="field-value">{{
                  acta.datosGenerales.funcionarioPolicialSuscribe || "-"
                }}</span>
              </div>
              <div class="field-item">
                <span class="field-label">Usuario SIFCOP</span>
                <span class="field-value">{{
                  acta.datosGenerales.usuarioSifcop == 1 ? "Si" : "No" || "-"
                }}</span>
              </div>
              <div class="field-item">
                <span class="field-label">N° de Requerimiento Judicial</span>
                <span class="field-value">{{
                  acta.datosGenerales.numeroRequerimientoJudicial || "-"
                }}</span>
              </div>
              <div class="field-item">
                <span class="field-label">Fecha y Hora de Creación</span>
                <span class="field-value">{{
                  formatDateTime(acta.datosGenerales.fechaCreacion)
                }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- SECCIÓN: Persona registrada -->
        <div class="section-header">
          <span class="section-title">Datos de la persona registrada en el SIFCOP</span>
        </div>
        <div class="section-card">
          <div class="section-body">
            <div class="fields-grid">
              <div class="field-item">
                <span class="field-label">Nombre y Apellido</span>
                <span class="field-value">{{ acta.persona.nombreApellido || "-" }}</span>
              </div>
              <div class="field-item">
                <span class="field-label">DNI / Pasaporte / Documento N°</span>
                <span class="field-value">{{ acta.persona.documento || "-" }}</span>
              </div>
              <div class="field-item">
                <span class="field-label">Fecha de nacimiento</span>
                <span class="field-value">{{ acta.persona.fechaNacimiento || "-" }}</span>
              </div>
              <div class="field-item">
                <span class="field-label">Nacionalidad</span>
                <span class="field-value">{{ acta.persona.nacionalidad || "-" }}</span>
              </div>
              <div class="field-item col-full">
                <span class="field-label">Domicilio</span>
                <span class="field-value">{{ acta.persona.domicilio || "-" }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- SECCIÓN: Vehículos / Armas / Elementos -->
        <div class="section-header">
          <span class="section-title">
            Datos de vehículos (A) / Armas Registrada (B) / Elementos (C) en el SIFCOP
          </span>
        </div>
        <div class="section-card">
          <div class="section-body subsections-body">
            <!-- Subsección A: Vehículos -->
            <div class="subsection">
              <div class="subsection-label-row">
                <span class="subsection-letter">A</span>
              </div>
              <div v-for="(vehiculo, idx) in acta.vehiculos" :key="`v-${idx}`" class="item-card">
                <span class="item-num">Vehículo {{ idx + 1 }}</span>
                <div class="fields-grid">
                  <div class="field-item">
                    <span class="field-label">Dominio</span>
                    <span class="field-value">{{ vehiculo.dominio || "-" }}</span>
                  </div>
                  <div class="field-item">
                    <span class="field-label">Año</span>
                    <span class="field-value">{{ vehiculo.anio || "-" }}</span>
                  </div>
                  <div class="field-item">
                    <span class="field-label">Tipo Vehículo</span>
                    <span class="field-value">{{ vehiculo.tipoVehiculo || "-" }}</span>
                  </div>
                  <div class="field-item">
                    <span class="field-label">Marca</span>
                    <span class="field-value">{{ vehiculo.marca || "-" }}</span>
                  </div>
                  <div class="field-item">
                    <span class="field-label">Número de motor</span>
                    <span class="field-value">{{ vehiculo.numeroMotor || "-" }}</span>
                  </div>
                  <div class="field-item">
                    <span class="field-label">Número de chasis</span>
                    <span class="field-value">{{ vehiculo.numeroChasis || "-" }}</span>
                  </div>
                  <div class="field-item">
                    <span class="field-label">Modelo</span>
                    <span class="field-value">{{ vehiculo.modelo || "-" }}</span>
                  </div>
                  <div class="field-item">
                    <span class="field-label">Color</span>
                    <span class="field-value">{{ vehiculo.color || "-" }}</span>
                  </div>
                </div>
              </div>
              <p v-if="!acta.vehiculos.length" class="empty-msg">Sin vehículos registrados.</p>
            </div>

            <!-- Subsección B: Armas -->
            <div class="subsection">
              <div class="subsection-label-row">
                <span class="subsection-letter">B</span>
              </div>
              <div v-for="(arma, idx) in acta.armas" :key="`a-${idx}`" class="item-card">
                <span class="item-num">Arma {{ idx + 1 }}</span>
                <div class="fields-grid">
                  <div class="field-item">
                    <span class="field-label">Tipo de arma</span>
                    <span class="field-value">{{ arma.tipoArma || "-" }}</span>
                  </div>
                  <div class="field-item">
                    <span class="field-label">Marca</span>
                    <span class="field-value">{{ arma.marca || "-" }}</span>
                  </div>
                  <div class="field-item">
                    <span class="field-label">Modelo</span>
                    <span class="field-value">{{ arma.modelo || "-" }}</span>
                  </div>
                  <div class="field-item">
                    <span class="field-label">Calibre</span>
                    <span class="field-value">{{ arma.calibre || "-" }}</span>
                  </div>
                  <div class="field-item">
                    <span class="field-label">Número de serie</span>
                    <span class="field-value">{{ arma.numeroSerie || "-" }}</span>
                  </div>
                </div>
              </div>
              <p v-if="!acta.armas.length" class="empty-msg">Sin armas registradas.</p>
            </div>

            <!-- Subsección C: Elementos -->
            <div class="subsection">
              <div class="subsection-label-row">
                <span class="subsection-letter">C</span>
              </div>
              <div v-for="(elemento, idx) in acta.elementos" :key="`e-${idx}`" class="item-card">
                <span class="item-num">Elemento {{ idx + 1 }}</span>
                <div class="fields-grid">
                  <div class="field-item">
                    <span class="field-label">Tipo de elemento</span>
                    <span class="field-value">{{ elemento.tipoElemento || "-" }}</span>
                  </div>
                  <div class="field-item">
                    <span class="field-label">Identificación</span>
                    <span class="field-value">{{ elemento.identificacion || "-" }}</span>
                  </div>
                  <div class="field-item">
                    <span class="field-label">Modelo</span>
                    <span class="field-value">{{ elemento.modelo || "-" }}</span>
                  </div>
                  <div class="field-item col-full">
                    <span class="field-label">Descripción</span>
                    <span class="field-value">{{ elemento.descripcion || "-" }}</span>
                  </div>
                </div>
              </div>
              <p v-if="!acta.elementos.length" class="empty-msg">Sin elementos registrados.</p>
            </div>
          </div>
        </div>
      </div>
    </template>

    <CustomModal
      ref="errorModalRef"
      :modalType="errorModalData.modalType"
      :title="errorModalData.title"
      :message="errorModalData.message"
      :showSecondaryButton="errorModalData.showSecondaryButton"
      :buttonLabels="errorModalData.buttonLabels"
      @confirm="errorModalData.onConfirm"
      @cancel="errorModalData.onCancel"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { storeToRefs } from "pinia";
import { Pencil, Trash, Check, X } from "lucide-vue";
import ProgressSpinner from "primevue/progressspinner";
import { useRouterCompat } from "@/composables";
import { useBreadcrumbStore } from "@/store/Pinia/useBreadcrumbStore";
import { useSifcopDetailStore } from "@/store/Pinia/useSifcopDetailStore";
import SifcopForm from "@/components/forms/SifcopForm.vue";
import { mapBackendDocToForm, useSifcopStore } from "@/store/Pinia/useSifcopStore";
import { generateSifcopPdf } from "@/utils/sifcopPdf";
import CustomModal from "@/components/CustomModal.vue";

const { router, route } = useRouterCompat();
const breadcrumbStore = useBreadcrumbStore();
const detailStore = useSifcopDetailStore();
const sifcopStore = useSifcopStore();
const { acta, rawDoc, isLoading, fetchError } = storeToRefs(detailStore);
const isEditing = ref(false);
const isBusy = computed(() => isLoading.value || sifcopStore.isLoading);
const errorModalRef = ref(null);
const errorModalData = ref({
  title: "No se pudo guardar el acta",
  message: "",
  modalType: "info",
  showSecondaryButton: false,
  buttonLabels: ["Aceptar"],
  onConfirm: () => errorModalRef.value?.close(),
  onCancel: () => errorModalRef.value?.close(),
});

const openErrorModal = (message) => {
  errorModalData.value = {
    ...errorModalData.value,
    title: "No se pudo completar la acción",
    message: message || "Ocurrió un error al actualizar el acta SIFCOP.",
    modalType: "error",
    showSecondaryButton: false,
    buttonLabels: ["Aceptar"],
    onConfirm: () => errorModalRef.value?.close(),
    onCancel: () => errorModalRef.value?.close(),
  };
  errorModalRef.value?.open();
};

const formatDate = (value) => {
  if (!value) return "-";
  const parts = value.split("-");
  if (parts.length !== 3) return value;
  const [year, month, day] = parts;
  return `${day}/${month}/${year}`;
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const [datePart, timePart] = value.split(" ");
  if (!datePart) return value;
  return timePart ? `${formatDate(datePart)} ${timePart.slice(0, 5)}` : formatDate(datePart);
};

const handleBack = () => {
  router.push("/sifcop");
};

const openDeleteConfirmModal = () => {
  errorModalData.value = {
    ...errorModalData.value,
    title: "Eliminar acta SIFCOP",
    message: "¿Estás seguro de que querés eliminar este acta? Esta acción no se puede deshacer.",
    modalType: "delete",
    showSecondaryButton: true,
    buttonLabels: ["Eliminar", "Cancelar"],
    onConfirm: async () => {
      errorModalRef.value?.close();
      const deleted = await detailStore.deleteById(route.params?.id);
      if (deleted) {
        router.push("/sifcop");
        return;
      }
      openErrorModal("No se pudo eliminar el acta SIFCOP.");
    },
    onCancel: () => errorModalRef.value?.close(),
  };
  errorModalRef.value?.open();
};

const handleDelete = () => {
  if (isBusy.value) return;
  openDeleteConfirmModal();
};

const handleEdit = () => {
  if (!rawDoc.value) return;
  sifcopStore.hydrateFormFromBackendDoc(rawDoc.value);
  sifcopStore.startEdit();
  sifcopStore.validateForm();
  isEditing.value = true;
};

const handleCancelEdit = () => {
  isEditing.value = false;
  sifcopStore.startView();
};

const handleSaveEdit = async () => {
  const result = await sifcopStore.handleUpdate(route.params?.id);
  if (!result?.ok) {
    openErrorModal();
    return;
  }

  await detailStore.fetchById(route.params?.id);
  isEditing.value = false;
  sifcopStore.startView();
};

const isGeneratingPdf = ref(false);

const handleGeneratePdf = async () => {
  if (isGeneratingPdf.value || !rawDoc.value) return;
  isGeneratingPdf.value = true;
  try {
    await generateSifcopPdf({
      formData: mapBackendDocToForm(rawDoc.value),
      outputFileName: `sifcop-${acta.value?.identificador || acta.value?.id || Date.now()}.pdf`,
    });
  } catch (err) {
    console.error("Error al generar PDF SIFCOP:", err);
  } finally {
    isGeneratingPdf.value = false;
  }
};

onMounted(async () => {
  breadcrumbStore.setItems([
    { label: "SIFCOP", to: "/sifcop" },
    { label: "Detalle del formulario", to: route.fullPath },
  ]);
  detailStore.clear();
  await detailStore.fetchById(route.params?.id);
});
</script>

<style scoped>
.sifcop-container {
  width: 100%;
  padding: 0 2rem 2rem;
}

.edit-mode-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.edit-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.75rem;
}

.button {
  min-width: 130px;
  min-height: 43px;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  border: none;
  border-radius: 6px;
}

.primaryButton {
  color: white;
  background-color: #0b5195;
}

.primaryButton:hover {
  background-color: #1366b0;
}

.secondaryButton {
  background-color: white;
  color: #0b5195;
  border: 1px #0b5195 solid;
}

.secondaryButton:hover {
  background-color: rgba(255, 255, 255, 0.267);
  color: #1366b0;
}

.headerBlock {
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
}

.title {
  font-weight: 700;
  font-size: 28px;
  line-height: 32px;
  color: #000000;
  margin: 0 0 0.5rem;
}

.subtitle {
  font-size: 16px;
  line-height: 24px;
  color: #4b5563;
  margin: 0;
}

.section-card {
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0px 4px 4px 0px #00000040;
  overflow: hidden;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #ffffff;
}

.section-title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #111827;
  margin-top: 1rem;
}

.section-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-shrink: 0;
}

.action-icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 48px;
  min-height: 43px;
  padding: 0;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  background-color: #d20f39;
  border: none;
  border-radius: 6px;
  color: #ffffff;
  cursor: pointer;
}

.action-icon-btn:hover {
  background-color: #c62828 !important;
}

.action-edit-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 130px;
  min-height: 43px;
  padding: 0;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  background-color: #0b5195;
  border: none;
  border-radius: 6px;
  color: #ffffff;
  font-weight: 500;
  cursor: pointer;
}

.action-edit-btn:hover {
  background-color: #09437d !important;
}

.pdfButton {
  display: flex;
  min-width: 155px;
  min-height: 43px;
  padding: 0;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  border-radius: 6px;
  box-shadow: 0px 1px 3px 0px rgba(166, 175, 195, 0.4);
  white-space: nowrap;
  background: #fff;
  border: 1px solid #0b5195;
  color: #0b5195;
}

.pdfButton:hover {
  opacity: 0.9;
  cursor: pointer;
  background: #f4f4f4 !important;
  color: #0b5195 !important;
}

.section-body {
  padding: 1.25rem;
}
.fields-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1.1rem 1.5rem;
}

.field-item {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
}

.col-full {
  grid-column: 1 / -1;
}

.field-label {
  font-size: 16px;
  font-weight: 700;
  color: #000000;
  line-height: 1.3;
}

.field-value {
  font-size: 16px;
  color: #000000;
  word-break: break-word;
  line-height: 1.4;
}

.subsections-body {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.subsection {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  background: #fafafa;
}

.subsection-label-row {
  display: flex;
  align-items: center;
}

.subsection-letter {
  font-size: 1rem;
  font-weight: 700;
  color: #374151;
}

.item-card {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 0.875rem;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.item-num {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #0b5195;
}

.empty-msg {
  color: #6b7280;
  font-size: 0.875rem;
  margin: 0;
  font-style: italic;
}

.loading-block {
  display: flex;
  justify-content: center;
  padding: 3rem 0;
}

.error-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2rem 0;
}

.error-text {
  color: #d20f39;
}

.back-btn {
  background-color: #0b5195;
  border: none;
  border-radius: 6px;
}

@media (max-width: 1100px) {
  .fields-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .fields-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .section-body {
    padding: 1rem;
  }
}

@media (max-width: 480px) {
  .fields-grid {
    grid-template-columns: 1fr;
  }
}
</style>
